import React, { useMemo } from 'react';

interface Resource {
  id: string;
  name: string;
  type: string;
  resourceGroup: string;
  subscriptionId: string;
  tags: Record<string, string>;
}

interface Props {
  resources: Resource[];
  allResources: Resource[];
}

const TagStatistics: React.FC<Props> = ({ resources, allResources }) => {
  const stats = useMemo(() => {
    const data = allResources.length > 0 ? allResources : resources;
    const total = data.length;
    const tagged = data.filter(r => Object.keys(r.tags).length > 0).length;
    const untagged = total - tagged;
    const totalTagCount = data.reduce((sum, r) => sum + Object.keys(r.tags).length, 0);
    const avg = total === 0 ? 0 : (totalTagCount / total).toFixed(1);

    // Tag frequency
    const freq: Record<string, number> = {};
    const valueMap: Record<string, Record<string, number>> = {};
    data.forEach(r => {
      Object.entries(r.tags).forEach(([k, v]) => {
        freq[k] = (freq[k] || 0) + 1;
        if (!valueMap[k]) valueMap[k] = {};
        valueMap[k][v] = (valueMap[k][v] || 0) + 1;
      });
    });

    const topTags = Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([key, count]) => ({
        key,
        count,
        pct: Math.round((count / total) * 100),
        topValues: Object.entries(valueMap[key] || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([v]) => v)
      }));

    // By type
    const byType: Record<string, number> = {};
    data.forEach(r => {
      const t = r.type.split('/').pop() || r.type;
      byType[t] = (byType[t] || 0) + 1;
    });
    const topTypes = Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 6);

    // By subscription
    const bySub: Record<string, number> = {};
    data.forEach(r => { bySub[r.subscriptionId] = (bySub[r.subscriptionId] || 0) + 1; });

    // Environment distribution
    const envDist: Record<string, number> = {};
    data.forEach(r => {
      const env = r.tags['Environment'] || 'Untagged';
      envDist[env] = (envDist[env] || 0) + 1;
    });

    return { total, tagged, untagged, avg, topTags, topTypes, bySub, envDist };
  }, [resources, allResources]);

  const envColors: Record<string, string> = {
    Production: '#dc3545',
    Development: '#28a745',
    Testing: '#007bff',
    Untagged: '#666',
  };

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Resources', value: stats.total, color: '#007bff' },
          { label: 'Tagged', value: stats.tagged, color: '#28a745' },
          { label: 'Untagged', value: stats.untagged, color: '#dc3545' },
          { label: 'Avg Tags / Resource', value: stats.avg, color: '#ffc107' },
        ].map(card => (
          <div key={card.label} style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: card.color }}>{card.value}</div>
            <div style={{ color: '#b0b0b0', fontSize: '0.85rem', marginTop: '0.25rem' }}>{card.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Top tags */}
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem' }}>
          <h3 style={{ margin: '0 0 1rem', color: '#f0f0f0', fontSize: '0.95rem' }}>Most Used Tags</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {stats.topTags.map(tag => (
              <div key={tag.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                  <span style={{ color: '#f0f0f0', fontSize: '0.85rem' }}>{tag.key}</span>
                  <span style={{ color: '#888', fontSize: '0.8rem' }}>{tag.count} ({tag.pct}%)</span>
                </div>
                <div style={{ height: '8px', background: '#333', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${tag.pct}%`, background: '#007bff', borderRadius: '4px', transition: 'width 0.4s ease' }} />
                </div>
                <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.15rem' }}>
                  {tag.topValues.join(' \u00b7 ')}
                </div>
              </div>
            ))}
            {stats.topTags.length === 0 && <p style={{ color: '#888', margin: 0, fontSize: '0.875rem' }}>No tags found</p>}
          </div>
        </div>

        {/* Resource types */}
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem' }}>
          <h3 style={{ margin: '0 0 1rem', color: '#f0f0f0', fontSize: '0.95rem' }}>Resources by Type</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {stats.topTypes.map(([type, count]) => {
              const pct = Math.round((count / stats.total) * 100);
              return (
                <div key={type}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                    <span style={{ color: '#f0f0f0', fontSize: '0.85rem' }}>{type}</span>
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>{count}</span>
                  </div>
                  <div style={{ height: '8px', background: '#333', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#28a745', borderRadius: '4px' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Environment distribution */}
      <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: '#f0f0f0', fontSize: '0.95rem' }}>Environment Distribution</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.entries(stats.envDist).map(([env, count]) => {
            const pct = Math.round((count / stats.total) * 100);
            const color = envColors[env] || '#888';
            return (
              <div key={env} style={{ flex: 1, minWidth: '120px', background: '#1a1a1e', borderRadius: '8px', padding: '1rem', textAlign: 'center', border: `1px solid ${color}44` }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color }}>{count}</div>
                <div style={{ color: '#b0b0b0', fontSize: '0.82rem', margin: '0.2rem 0' }}>{env}</div>
                <div style={{ height: '4px', background: '#333', borderRadius: '2px', overflow: 'hidden', margin: '0.5rem 0 0' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '2px' }} />
                </div>
                <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.2rem' }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TagStatistics;
